import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, Clock, TrendingUp, MapPin, Users, Calendar, MessageCircle, CheckCircle, XCircle, Loader2, Sparkles, ArrowRight, Send, RotateCcw, BookmarkPlus, Bookmark } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const EXAMPLE_PROMPTS = [
  '7 days in Spiti, motorcycle trip, love high passes and monasteries',
  'Quiet weekend getaway near Delhi for a couple, riverside, budget ₹15K',
  'First time Northeast, 5 days, want culture and waterfalls',
  '4 days in Tirthan-Jibhi with kids, easy walks, home-cooked food',
  'Luxury 5-day Kumaon drive, heritage bungalows, mountain views',
  '10 days across Himachal, mix of adventure and slow travel',
];

const DIFFICULTY_COLORS = {
  Easy: 'bg-green-100 text-green-800',
  Moderate: 'bg-amber-100 text-amber-800',
  Challenging: 'bg-red-100 text-red-800',
};

function SavedPlans({ plans, onLoad, onDelete }) {
  if (plans.length === 0) return null;
  return (
    <div className="mt-8">
      <p className="text-xs tracking-[0.3em] uppercase text-stone-400 mb-3">Your Saved Plans</p>
      <div className="space-y-2">
        {plans.map((p, i) => (
          <div key={i} className="flex items-center justify-between bg-white rounded-lg border border-stone-100 p-3 hover:shadow-sm transition-shadow">
            <button onClick={() => onLoad(p)} className="flex-1 text-left">
              <p className="text-sm font-medium text-stone-800 truncate" style={pf}>{p.itinerary.title}</p>
              <p className="text-[10px] text-stone-400">{p.itinerary.duration} days · {p.itinerary.region} · {p.query.substring(0, 50)}...</p>
            </button>
            <button onClick={() => onDelete(i)} className="text-stone-300 hover:text-red-400 transition-colors p-1 ml-2"><XCircle className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RenderedItinerary({ data, onRefine, sessionId, query }) {
  const [openDays, setOpenDays] = useState([1, 2]);
  const [saved, setSaved] = useState(false);

  const toggleDay = (d) => setOpenDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);

  const saveToLocal = () => {
    const plans = JSON.parse(localStorage.getItem('gh_plans') || '[]');
    plans.unshift({ itinerary: data, query, sessionId, savedAt: new Date().toISOString() });
    if (plans.length > 10) plans.pop();
    localStorage.setItem('gh_plans', JSON.stringify(plans));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const whatsappText = encodeURIComponent(
    `Hi! I'd like to book this itinerary:\n\n*${data.title}*\n${data.duration} days · ${data.region}\n${data.priceEstimate || ''}\n\n` +
    (data.days || []).map(d => `Day ${d.day}: ${d.title} (${d.location})`).join('\n') +
    `\n\ngohimalayan.com/plan`
  );
  const whatsappUrl = `https://wa.me/919871155963?text=${whatsappText}`;

  return (
    <div className="mt-10 animate-in fade-in" data-testid="rendered-itinerary">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            {data.difficulty && <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${DIFFICULTY_COLORS[data.difficulty] || 'bg-stone-100 text-stone-600'}`}>{data.difficulty}</span>}
            <span className="text-[10px] text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{data.region}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-light italic text-stone-900 mb-1" style={pf}>{data.title}</h2>
          {data.subtitle && <p className="text-sm text-stone-400 italic" style={pf}>{data.subtitle}</p>}
        </div>
        <button onClick={saveToLocal} className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-medium transition-all ${saved ? 'bg-[#2C5F8D] text-white border-[#2C5F8D]' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'}`} data-testid="save-plan-btn">
          {saved ? <><Bookmark className="w-3.5 h-3.5" />Saved</> : <><BookmarkPlus className="w-3.5 h-3.5" />Save Plan</>}
        </button>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-x-5 gap-y-2 mb-5">
        {data.duration && <div className="flex items-center gap-1.5 text-xs text-stone-500"><Clock className="w-3.5 h-3.5 text-stone-400" />{data.duration} days</div>}
        {data.maxAltitude && <div className="flex items-center gap-1.5 text-xs text-stone-500"><TrendingUp className="w-3.5 h-3.5 text-stone-400" />{data.maxAltitude}</div>}
        {data.bestSeason && <div className="flex items-center gap-1.5 text-xs text-stone-500"><Calendar className="w-3.5 h-3.5 text-stone-400" />{data.bestSeason}</div>}
        {data.priceEstimate && <div className="flex items-center gap-1.5 text-xs text-stone-600 font-semibold">{data.priceEstimate}</div>}
      </div>

      {/* Description */}
      {data.description && <p className="text-stone-600 text-sm leading-relaxed mb-6" style={pf}>{data.description}</p>}

      {/* Vibes */}
      {data.vibes && (
        <div className="flex flex-wrap gap-1.5 mb-8">
          {data.vibes.map((v, i) => <span key={i} className="text-[10px] text-stone-500 bg-stone-100 px-2.5 py-1 rounded-full capitalize">{v.replace('-', ' ')}</span>)}
        </div>
      )}

      {/* Day by Day */}
      {data.days && data.days.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-stone-900" style={pf}>Day by Day</h3>
            <div className="flex gap-2 text-[10px] text-stone-400">
              <button onClick={() => setOpenDays(data.days.map(d => d.day))} className="hover:text-stone-700 transition-colors">Expand all</button>
              <span>|</span>
              <button onClick={() => setOpenDays([])} className="hover:text-stone-700 transition-colors">Collapse all</button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute left-[29px] top-6 bottom-6 w-px bg-stone-200" />
            <div className="space-y-3">
              {data.days.map(day => (
                <div key={day.day} className="border border-stone-100 rounded-xl overflow-hidden bg-white hover:shadow-sm transition-shadow" data-testid={`plan-day-${day.day}`}>
                  <button onClick={() => toggleDay(day.day)} className="w-full flex items-center gap-4 p-5 text-left">
                    <div className="w-10 h-10 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2C5F8D] text-sm font-bold">{String(day.day).padStart(2, '0')}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-semibold text-stone-900 truncate" style={pf}>{day.title}</h4>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span className="text-xs text-stone-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{day.location}</span>
                        {day.altitude && <span className="text-xs text-stone-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{day.altitude}</span>}
                        {day.drive && <span className="text-xs text-stone-400">{day.drive}</span>}
                      </div>
                    </div>
                  </button>
                  {openDays.includes(day.day) && (
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-14">
                        <p className="text-sm text-stone-600 leading-relaxed mb-4" style={pf}>{day.description}</p>
                        {day.stay && (
                          <div className="flex items-start gap-2 mb-3 bg-[#2C5F8D]/5 rounded-lg p-3">
                            <Mountain className="w-4 h-4 text-[#2C5F8D] flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-semibold text-[#2C5F8D]">Stay</p>
                              {day.staySlug ? (
                                <Link to={`/stay/${day.staySlug}`} className="text-sm text-stone-700 hover:text-[#2C5F8D] transition-colors underline underline-offset-2">{day.stay}</Link>
                              ) : (
                                <p className="text-sm text-stone-700">{day.stay}</p>
                              )}
                            </div>
                          </div>
                        )}
                        {day.highlights && (
                          <div className="flex flex-wrap gap-1.5">
                            {day.highlights.map((h, i) => <span key={i} className="text-[10px] text-stone-500 bg-stone-50 border border-stone-100 px-2.5 py-1 rounded-full">{h}</span>)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Included */}
      {data.included && (
        <div className="bg-white rounded-xl border border-stone-100 p-5 mb-8">
          <h3 className="text-sm font-semibold text-stone-900 mb-3" style={pf}>Typically Included</h3>
          <ul className="grid sm:grid-cols-2 gap-2">
            {data.included.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-stone-600"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-medium py-3.5 rounded-xl hover:bg-[#1fb855] transition-colors" data-testid="book-plan-cta">
          <MessageCircle className="w-4 h-4" />Book This Plan via WhatsApp
        </a>
        <button onClick={onRefine} className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-stone-200 text-stone-600 text-sm font-medium hover:bg-stone-50 transition-colors" data-testid="refine-plan-btn">
          <RotateCcw className="w-4 h-4" />Refine This Plan
        </button>
      </div>
    </div>
  );
}

export default function TripPlanner() {
  const [query, setQuery] = useState('');
  const [refinement, setRefinement] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [originalQuery, setOriginalQuery] = useState('');
  const [error, setError] = useState(null);
  const [refineMode, setRefineMode] = useState(false);
  const [savedPlans, setSavedPlans] = useState([]);
  const inputRef = useRef(null);
  const refineRef = useRef(null);

  useEffect(() => {
    const plans = JSON.parse(localStorage.getItem('gh_plans') || '[]');
    setSavedPlans(plans);
  }, [result]);

  const generate = async (queryText, refineText = null) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/itineraries/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: queryText,
          session_id: sessionId,
          refinement: refineText,
        }),
      });
      if (!res.ok) throw new Error('Failed to generate itinerary');
      const data = await res.json();
      setResult(data.itinerary);
      setSessionId(data.session_id);
      if (!refineText) setOriginalQuery(queryText);
      setRefineMode(false);
      setRefinement('');
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    generate(query.trim());
  };

  const handleRefineSubmit = (e) => {
    e.preventDefault();
    if (!refinement.trim()) return;
    generate(originalQuery, refinement.trim());
  };

  const handlePromptClick = (prompt) => {
    setQuery(prompt);
    generate(prompt);
  };

  const startOver = () => {
    setResult(null);
    setSessionId(null);
    setQuery('');
    setOriginalQuery('');
    setRefineMode(false);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const loadPlan = (plan) => {
    setResult(plan.itinerary);
    setOriginalQuery(plan.query);
    setSessionId(plan.sessionId);
    setQuery(plan.query);
  };

  const deletePlan = (idx) => {
    const plans = JSON.parse(localStorage.getItem('gh_plans') || '[]');
    plans.splice(idx, 1);
    localStorage.setItem('gh_plans', JSON.stringify(plans));
    setSavedPlans(plans);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="trip-planner-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Mountain className="w-5 h-5 text-[#2C5F8D]" />
            <span className="text-sm font-bold tracking-tight text-stone-800" style={pf}>GoHimalayan</span>
          </Link>
          <div className="flex items-center gap-5 text-xs text-stone-500">
            <Link to="/itineraries" className="hover:text-stone-800 transition-colors">Itineraries</Link>
            <span className="text-[#2C5F8D] font-semibold">AI Planner</span>
            <Link to="/discovered" className="hover:text-stone-800 transition-colors hidden sm:inline">Stays</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        {/* Hero — only show when no result */}
        {!result && !loading && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#2C5F8D]" />
              <p className="text-xs tracking-[0.3em] uppercase text-stone-400">AI Trip Planner</p>
            </div>
            <h1 className="text-3xl sm:text-4xl font-light italic text-stone-900 leading-tight mb-4" style={pf}>
              Tell us your dream trip.<br />We'll plan it.
            </h1>
            <p className="text-stone-500 text-base max-w-xl" style={pf}>
              Describe your ideal Himalayan journey — duration, budget, vibe, interests — and our AI will create a day-by-day itinerary with curated stays.
            </p>
          </div>
        )}

        {/* Input Form */}
        {!result && !loading && (
          <>
            <form onSubmit={handleSubmit} className="mb-8" data-testid="planner-form">
              <div className="relative">
                <textarea
                  ref={inputRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="e.g., 7 days in Himachal, love mountains and motorcycles, budget ₹40K..."
                  className="w-full h-32 p-5 pr-14 rounded-2xl border border-stone-200 bg-white text-stone-800 text-sm leading-relaxed resize-none focus:outline-none focus:border-[#2C5F8D] focus:ring-1 focus:ring-[#2C5F8D]/20 transition-all placeholder:text-stone-300"
                  data-testid="planner-input"
                />
                <button type="submit" disabled={!query.trim()} className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#2C5F8D] text-white flex items-center justify-center hover:bg-[#1e4a6e] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid="planner-submit">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Example Prompts */}
            <div className="mb-8">
              <p className="text-xs text-stone-400 mb-3">Try something like:</p>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPTS.map((p, i) => (
                  <button key={i} onClick={() => handlePromptClick(p)} className="text-xs text-stone-500 bg-white border border-stone-150 px-3 py-2 rounded-full hover:border-[#2C5F8D] hover:text-[#2C5F8D] transition-all text-left" data-testid={`example-prompt-${i}`}>{p}</button>
                ))}
              </div>
            </div>

            {/* Saved Plans */}
            <SavedPlans plans={savedPlans} onLoad={loadPlan} onDelete={deletePlan} />
          </>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20" data-testid="planner-loading">
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-[#2C5F8D] animate-spin" />
              </div>
            </div>
            <p className="text-stone-600 text-sm font-medium mb-1" style={pf}>Planning your journey...</p>
            <p className="text-stone-400 text-xs">Mapping routes, finding stays, checking seasons</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-5 mb-6 text-center">
            <p className="text-red-600 text-sm mb-2">{error}</p>
            <button onClick={startOver} className="text-red-500 text-xs underline">Try again</button>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <>
            {/* Original query reminder */}
            <div className="flex items-center justify-between mb-2 mt-4">
              <p className="text-xs text-stone-400"><span className="text-stone-500 font-medium">Your request:</span> {originalQuery}</p>
              <button onClick={startOver} className="text-xs text-[#2C5F8D] hover:underline flex items-center gap-1">
                <Sparkles className="w-3 h-3" />New Plan
              </button>
            </div>

            <RenderedItinerary
              data={result}
              sessionId={sessionId}
              query={originalQuery}
              onRefine={() => {
                setRefineMode(true);
                setTimeout(() => refineRef.current?.focus(), 100);
              }}
            />

            {/* Refine Input */}
            {refineMode && (
              <form onSubmit={handleRefineSubmit} className="mt-6" data-testid="refine-form">
                <p className="text-xs text-stone-500 mb-2">How would you like to change it?</p>
                <div className="relative">
                  <input
                    ref={refineRef}
                    value={refinement}
                    onChange={e => setRefinement(e.target.value)}
                    placeholder="e.g., make it shorter, add Spiti, more luxury stays, skip Day 3..."
                    className="w-full p-4 pr-12 rounded-xl border border-stone-200 bg-white text-stone-800 text-sm focus:outline-none focus:border-[#2C5F8D] focus:ring-1 focus:ring-[#2C5F8D]/20 transition-all placeholder:text-stone-300"
                    data-testid="refine-input"
                  />
                  <button type="submit" disabled={!refinement.trim()} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#2C5F8D] text-white flex items-center justify-center hover:bg-[#1e4a6e] transition-colors disabled:opacity-30" data-testid="refine-submit">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </form>
            )}

            {/* Link to curated */}
            <div className="mt-10 text-center">
              <p className="text-stone-400 text-xs mb-2">Want a ready-to-go itinerary instead?</p>
              <Link to="/itineraries" className="text-[#2C5F8D] text-sm font-medium hover:underline flex items-center justify-center gap-1">
                Browse curated itineraries <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-6 px-6 bg-white">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="w-4 h-4 text-stone-400" />
            <span className="text-xs text-stone-400">© 2026 GoHimalayan</span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-stone-400">
            <Link to="/privacy" className="hover:text-stone-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-600 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
